import { UserRoundCheck } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { selectColumn } from "components/BaseTableV3/selectColumn";
import RuvixxDate from "components/RuvixxDate";
import TagList from "components/TagList";
import { TableLink } from "components/BaseTableV3/TableLink";
import { LeadsTableActions } from "routes/leads/table/LeadsTableActions";
import { leadDetailHref } from "routes/leads/table/leadDetailHref";

// Matches the Lead polymorphic identity and BaseTableV3's className.
const TAG_MODEL_TYPE = "Lead";

const ContactCell = ({ contactId }) =>
  contactId ? (
    <TableLink href={`/contacts/${contactId}`}>
      <Badge variant="outline" className="tw:gap-1.5">
        <UserRoundCheck className="tw:max-h-3.5" />
        Contact
      </Badge>
    </TableLink>
  ) : (
    <span className="tw:text-[var(--color-text-muted)]">—</span>
  );

const StatusBadge = ({ status }) => {
  if (!status) return null;
  return (
    <Badge variant="outline" className="tw:gap-1.5">
      {status.color && (
        <span
          className="tw:inline-block tw:size-2 tw:rounded-full"
          style={{ backgroundColor: status.color }}
        />
      )}
      {status.name}
    </Badge>
  );
};

export const getLeadsTableColumns = ({
  scope,
  events = [],
  updateStatus,
  deleteLead,
  invalidateData,
}) => {
  return [
    selectColumn,
    {
      header: "ID",
      id: "ID",
      accessorKey: "id",
      size: 80,
      cell: ({ row }) => {
        const href = leadDetailHref(row.original);
        const id = row.getValue("ID");
        return href ? <TableLink href={href}>{id}</TableLink> : id;
      },
    },
    {
      header: "Name",
      id: "Name",
      accessorKey: "name",
    },
    {
      header: "Email",
      id: "Email",
      accessorKey: "email",
    },
    {
      header: "Phone",
      id: "Phone",
      accessorKey: "phone",
    },
    {
      header: "Status",
      id: "Status",
      accessorKey: "status",
      enableSorting: false,
      cell: ({ row }) => <StatusBadge status={row.original?.status} />,
    },
    {
      header: "Source",
      id: "Source",
      accessorKey: "source",
      enableSorting: false,
    },
    {
      header: "Contact",
      id: "Contact",
      accessorKey: "contact_id",
      enableSorting: false,
      cell: ({ row }) => <ContactCell contactId={row.original.contact_id} />,
    },
    {
      header: "Tags",
      id: "Tags",
      accessorKey: "tags",
      enableSorting: false,
      cell: ({ row }) => (
        <TagList
          tags={row.original.tags}
          modelType={TAG_MODEL_TYPE}
          modelId={row.original.id}
          onUpdate={invalidateData}
          tableCell
        />
      ),
    },
    {
      header: "Created",
      id: "Created",
      accessorKey: "created_at",
      cell: ({ row }) => {
        const date = row.original?.created_at;
        return date ? <RuvixxDate date={date} /> : null;
      },
    },
    {
      header: <div></div>,
      id: "Actions",
      accessorKey: "actions",
      enableSorting: false,
      enableResizing: false,
      size: 60,
      meta: {
        isDisplay: true,
      },
      cell: ({ row }) => (
        <LeadsTableActions
          original={row.original}
          scope={scope}
          events={events}
          updateStatus={updateStatus}
          deleteLead={deleteLead}
        />
      ),
    },
  ];
};

export const getLeadsTableFilters = ({ events = [], tags = [] } = {}) => {
  return [
    {
      key: "status_id",
      title: "Status",
      type: "select",
      data: events.map(event => ({ id: event.id, name: event.name })),
    },
    {
      key: "tag_id",
      title: "Tag",
      type: "tag",
      data: tags,
    },
  ];
};
